import React, { useEffect, useState } from "react";
import { createSupportTicket, listMySupportTickets } from "../lib/api";

/**
 * SupportTicketsPanel — logged-in bug reports. Staff answers only via DM.
 */
export function SupportTicketsPanel({ onToast }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [category, setCategory] = useState("bug");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const r = await listMySupportTickets();
      setTickets(r.tickets || []);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't load tickets.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) return;
    setBusy(true);
    try {
      await createSupportTicket({
        category,
        subject: subject.trim(),
        body: body.trim(),
      });
      setSubject("");
      setBody("");
      onToast?.("Ticket submitted — we'll reply in your DMs.");
      await reload();
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Couldn't submit ticket.");
    } finally {
      setBusy(false);
    }
  };

  const statusLabel = (s) => {
    if (s === "answered") return "Answered (check DMs)";
    if (s === "in_progress") return "In progress";
    if (s === "closed") return "Closed";
    return "Open";
  };

  return (
    <div className="space-y-6" data-testid="support-tickets-panel">
      <div className="rounded-2xl px-4 py-3 border border-[#14b8a6]/30 bg-[#14b8a6]/5">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-life-ring mr-1" />REPORT A BUG
        </p>
        <p className="text-[11px] text-white/65 mt-1">
          Describe what went wrong. Our team replies only in your direct messages —
          not on this ticket thread.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-3" data-testid="support-ticket-form">
        <label className="block text-[10px] tracking-widest text-white/50">
          CATEGORY
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="support-ticket-category"
            className="mt-1 w-full bg-black/50 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
          >
            <option value="bug">Bug</option>
            <option value="billing">Billing</option>
            <option value="account">Account</option>
            <option value="feature">Feature request</option>
            <option value="abuse">Report abuse</option>
            <option value="other">Other</option>
          </select>
        </label>
        <label className="block text-[10px] tracking-widest text-white/50">
          SUBJECT
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={160}
            required
            data-testid="support-ticket-subject"
            className="mt-1 w-full bg-black/50 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
            placeholder="Short summary"
          />
        </label>
        <label className="block text-[10px] tracking-widest text-white/50">
          DETAILS
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
            rows={5}
            maxLength={5000}
            data-testid="support-ticket-body"
            className="mt-1 w-full bg-black/50 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
            placeholder="Steps to reproduce, what you expected, screenshots links if any…"
          />
        </label>
        <button
          type="submit"
          disabled={busy || !subject.trim() || !body.trim()}
          data-testid="support-ticket-submit"
          className="btn-neon px-6 py-3 rounded-2xl text-xs tracking-widest disabled:opacity-60"
        >
          {busy ? "SUBMITTING…" : "SUBMIT TICKET"}
        </button>
      </form>

      <div data-testid="support-ticket-list">
        <p className="text-[10px] tracking-widest text-white/45 mb-2">YOUR TICKETS</p>
        {loading ? (
          <p className="text-[12px] text-white/45">Loading…</p>
        ) : tickets.length === 0 ? (
          <p className="text-[12px] text-white/45" data-testid="support-tickets-empty">
            No tickets yet.
          </p>
        ) : (
          <div className="space-y-2">
            {tickets.map((t) => (
              <div
                key={t.ticket_id}
                data-testid={`support-ticket-${t.ticket_id}`}
                className="glass rounded-xl p-3 border border-white/10"
              >
                <div className="flex justify-between gap-2 items-start">
                  <div>
                    <p className="text-[10px] text-[#14b8a6] font-bold uppercase">{t.category}</p>
                    <p className="font-bold text-sm">{t.subject}</p>
                    <p className="text-[11px] text-white/55 mt-1 line-clamp-2">{t.body}</p>
                  </div>
                  <span
                    data-testid={`support-ticket-status-${t.ticket_id}`}
                    className="text-[9px] tracking-widest px-2 py-1 rounded-full bg-white/10 shrink-0"
                  >
                    {statusLabel(t.status)}
                  </span>
                </div>
                <p className="text-[10px] text-white/40 mt-2">
                  {t.created_at ? new Date(t.created_at).toLocaleString() : ""}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SupportTicketsPanel;
